import type { HardwareGraph, HardwareNode, HardwareNodeViewModel, HardwarePin, NodeKind } from '../types';

function widthLabel(width: number) {
  return width > 1 ? `[${width - 1}:0]` : '1b';
}

function operationLabel(node: HardwareNode) {
  const operation = String(node.properties.operation ?? node.label).toLowerCase();
  const labels: Record<string, string> = {
    add: '+', sub: '−', multiply: '×', mul: '×', shift_left: '<<', shift_right: '>>',
    and: '&', or: '|', xor: '^', not: '~', equal: '==', equality: '==', less_than: '<', greater_than: '>',
  };
  return labels[operation] ?? node.label;
}

function pinsFor(node: HardwareNode, connectedInputCount?: number): HardwarePin[] {
  if (node.type === 'input') return [{ id: 'out', label: 'OUT', direction: 'output', signalType: 'data' }];
  if (node.type === 'output') return [{ id: 'in', label: 'IN', direction: 'input', signalType: 'data' }];
  if (node.type === 'constant') return [{ id: 'out', label: 'Y', direction: 'output', signalType: 'data' }];
  if (node.type === 'register') {
    const pins: HardwarePin[] = [
      { id: 'd', label: 'D', direction: 'input', signalType: 'data' },
      { id: 'q', label: 'Q', direction: 'output', signalType: 'data' },
      { id: 'clk', label: 'CLK', direction: 'input', signalType: 'clock' },
    ];
    if (node.properties.enable) pins.push({ id: 'en', label: 'EN', direction: 'input', signalType: 'control' });
    if (node.properties.reset) pins.push({ id: 'rst', label: 'RST', direction: 'input', signalType: 'reset', inverted: node.properties.resetPolarity === 'active_low' });
    return pins;
  }
  if (node.type === 'mux') {
    const inputCount = Math.max(2, Number(node.properties.inputCount ?? 2));
    return [
      ...Array.from({ length: inputCount }, (_, index) => ({ id: `in-${index}`, label: `I${index}`, direction: 'input' as const, signalType: 'data' as const })),
      { id: 'sel', label: 'SEL', direction: 'input', signalType: 'control' },
      { id: 'y', label: 'Y', direction: 'output', signalType: 'data' },
    ];
  }
  if (node.type === 'logic') {
    const operation = String(node.properties.operation ?? node.label).toLowerCase();
    const inputCount = operation.includes('not') ? 1 : Math.max(2, connectedInputCount ?? Number(node.properties.inputCount ?? 2));
    return [
      ...Array.from({ length: inputCount }, (_, index) => ({ id: `in-${index}`, label: index < 26 ? String.fromCharCode(65 + index) : `I${index}`, direction: 'input' as const, signalType: 'data' as const })),
      { id: 'y', label: 'Y', direction: 'output', signalType: 'data' },
    ];
  }
  return [
    { id: 'a', label: 'A', direction: 'input', signalType: 'data' },
    { id: 'b', label: 'B', direction: 'input', signalType: 'data' },
    { id: 'y', label: 'Y', direction: 'output', signalType: 'data' },
  ];
}

export function toNodeViewModel(node: HardwareNode, connectedInputCount?: number): HardwareNodeViewModel {
  const metadata = node.type === 'logic' && connectedInputCount
    ? { ...node.properties, inputCount: connectedInputCount }
    : node.properties;
  return {
    id: node.id,
    kind: node.type as NodeKind,
    label: node.label,
    width: node.width,
    widthLabel: widthLabel(node.width),
    operation: operationLabel(node),
    pins: pinsFor(node, connectedInputCount),
    source: node.source,
    metadata,
    ir: node,
  };
}

export function graphViewModels(graph: HardwareGraph) {
  const connectedInputCounts = new Map<string, number>();
  graph.edges.forEach((edge) => {
    if ((edge.kind ?? 'data') !== 'data') return;
    connectedInputCounts.set(edge.target, (connectedInputCounts.get(edge.target) ?? 0) + 1);
  });
  return [...graph.ports, ...graph.nodes].map((node) => toNodeViewModel(node, connectedInputCounts.get(node.id)));
}
